import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

import { Alert } from '../../components';
import { Badge, Button, Card, StatePanel } from '../../components/ui';
import { usePoll } from '../../hooks/usePoll';

import './cooby-vm-settings.scss';

const API = '/api/admin/cooby-vms';

// This page polls. Without this, every failed poll fires the global axios
// error toast from contexts/User.jsx — "This item no longer exists." on a 404,
// once every few seconds, forever. The page reports its own failures inline,
// so the toast is both redundant and unbounded. `silent` suppresses only the
// toast; 401 refresh-and-replay still runs and the promise still rejects.
const QUIET = { metadata: { silent: true } };

// How often the seat list refreshes, and how often a QR is re-fetched.
// WhatsApp rotates the QR every 20-60s, so a slow poll would routinely show a
// code that is already dead by the time someone points a camera at it.
const LIST_POLL_MS = 10000;
const QR_POLL_MS = 4000;

// Presentation-only. This is not the shared business-status scale, so tones
// are resolved locally rather than pushed through Badge's `status` prop.
const STATE_UI = {
  connected:   { tone: 'success', label: 'Connected' },
  needs_qr:    { tone: 'warning', label: 'Needs scan' },
  linking:     { tone: 'info',    label: 'Syncing…' },
  broken:      { tone: 'danger',  label: 'Crashed' },
  no_wa_tab:   { tone: 'danger',  label: 'No WhatsApp tab' },
  chrome_down: { tone: 'danger',  label: 'Chrome down' },
  starting:    { tone: 'info',    label: 'Starting' },
};

// A seat with no state at all means the agent never answered — say that,
// rather than "Unknown", which reads like the agent replied and we could not
// interpret it.
const stateUi = (state) =>
  STATE_UI[state] || { tone: state ? 'muted' : 'danger', label: state || 'Agent unreachable' };

function formatAge(minutes) {
  if (minutes == null) return null;
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

/** Formats 447507785188 as +44 7507 785188 so it is readable at a glance. */
function formatPhone(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('44')) {
    return `+44 ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }
  return `+${digits}`;
}

/**
 * Is data actually arriving for this seat?
 *
 * Kept visually separate from the session state on purpose. A green WhatsApp
 * Web tab only proves the browser is logged in — Cooby has its own login in
 * that profile and can be silently dead behind a healthy-looking session.
 */
function IntakeLine({ intake }) {
  if (!intake) return null;

  if (intake.applicable === false) {
    return <p className="cvm-intake cvm-intake--muted">{intake.reason}</p>;
  }
  if (intake.unknown) {
    return <p className="cvm-intake cvm-intake--muted">Intake check unavailable ({intake.error})</p>;
  }
  if (intake.last_message_at == null) {
    return <p className="cvm-intake cvm-intake--bad">No messages ever recorded for this seat.</p>;
  }

  const stale = intake.age_minutes != null && intake.age_minutes > (intake.warn_after || 120);

  return (
    <p className={`cvm-intake ${stale ? 'cvm-intake--bad' : 'cvm-intake--ok'}`}>
      Last message received {formatAge(intake.age_minutes)}
      {stale ? ' — nothing is arriving.' : '.'}
    </p>
  );
}

function SeatCard({ seat, onRefresh }) {
  const session = seat.session;
  const state = session?.state;
  const needsQr = state === 'needs_qr';

  const [qr, setQr] = useState(null);
  const [qrExpired, setQrExpired] = useState(false);
  const [linkCode, setLinkCode] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  const fetchQr = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/${seat.id}/qr`, QUIET);
      if (res.data?.authenticated) {
        setQr(null);
        onRefresh();
        return;
      }
      // While WhatsApp's "Select to reload QR code" overlay is up, the agent
      // deliberately serves no QR: the dead code is still drawn on the canvas,
      // so anything it captured would look crisp and never scan.
      setQrExpired(!!res.data?.qr_expired);
      setQr(res.data?.qr_code?.qr_code || null);
      if (res.data?.link_code) setLinkCode(res.data.link_code);
    } catch {
      // Transient — the next poll will pick it up.
    }
  }, [seat.id, onRefresh]);

  // Auto-poll the QR whenever this seat is waiting to be linked, so the page
  // is useful the moment it is opened rather than after hunting for a button.
  useEffect(() => {
    if (!needsQr) {
      setQr(null);
      setQrExpired(false);
      setLinkCode(null);
      return undefined;
    }
    fetchQr();
    pollRef.current = setInterval(fetchQr, QR_POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [needsQr, fetchQr]);

  const act = async (action, label, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(label);
    setError('');
    try {
      const res = await axios.post(`${API}/${seat.id}/${action}`, null, QUIET);
      if (res.data?.link_code) setLinkCode(res.data.link_code);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || `Could not ${label.toLowerCase()}.`);
    } finally {
      setBusy(null);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(linkCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — type the code in manually.');
    }
  };

  const ui = stateUi(state);
  const phone = formatPhone(seat.phone);

  return (
    <Card className="cvm-card" padding="md">
      <div className="cvm-card__head">
        <div className="cvm-card__title">
          {/* cvm-seat-name exists purely to win a specificity fight — see the
              note in cooby-vm-settings.scss. */}
          <h3 className="cvm-seat-name">{seat.label}</h3>
          {/* Labelled, because the card title is now a human name for the
              group — without this the bare address reads as a contact rather
              than as the Cooby account the seat is bound to. */}
          <span className="cvm-seat-email">Cooby seat: {seat.seat_email}</span>
        </div>
        <Badge tone={seat.configured ? ui.tone : 'muted'}>
          {seat.configured ? ui.label : 'No agent'}
        </Badge>
      </div>

      <p className="cvm-meta">
        {seat.vm} · {seat.vm_host}
        {phone ? <> · <span className="cvm-phone">{phone}</span></> : null}
      </p>

      <IntakeLine intake={seat.intake} />

      {!seat.configured && (
        <Alert
          type="info"
          message="No agent configured for this VM"
          description="Its session cannot be seen or re-linked from here yet."
        />
      )}

      {seat.configured && seat.agent_error && (
        <Alert type="error" message="Cannot reach the VM agent" description={seat.agent_error} />
      )}

      {error && <Alert type="error" message={error} />}

      {/* The scan worked and WhatsApp is downloading the account's history. It
          shows neither a QR nor a chat list for minutes, which looks broken and
          invites a reload — and a reload here destroys the link. Say so. */}
      {state === 'linking' && (
        <Alert
          className="cvm-notice"
          type="info"
          message="Scanned — WhatsApp is syncing this account"
          description="This can take several minutes on a large account. Leave it alone: reloading now would undo the link. The card turns green on its own when it finishes."
        />
      )}

      {needsQr && (
        <div className="cvm-scan">
          {/* The single most important line on the page: scanning with the
              wrong phone silently syncs the wrong messages under this seat
              within ~3 minutes, and there is no undo. */}
          <Alert
            className="cvm-notice"
            type="warning"
            message={`Scan with ${phone} only`}
            description="Any other phone silently syncs the wrong messages under this seat within about three minutes, and there is no undo."
          />

          {qr ? (
            <img
              className="cvm-qr"
              src={`data:image/png;base64,${qr}`}
              alt={`WhatsApp linking QR code for ${seat.label}`}
            />
          ) : (
            <div className="cvm-qr cvm-qr--placeholder">
              {qrExpired ? 'That code expired — fetching a fresh one…' : 'Waiting for the QR…'}
            </div>
          )}

          <p className="cvm-hint">
            On WhatsApp: <strong>Linked devices</strong> → <strong>Link a device</strong>,
            then scan the code above. It refreshes automatically.
          </p>

          {/* You cannot scan a code shown on the same phone you are scanning
              with, so offer the typed alternative for the one-device case. */}
          {linkCode ? (
            <div className="cvm-code">
              <span className="cvm-code__value">{linkCode}</span>
              <Button variant="secondary" size="lg" fullWidth onClick={copyCode}>
                {copied ? 'Copied' : 'Copy code'}
              </Button>
              <p className="cvm-hint">
                On WhatsApp: <strong>Linked devices</strong> → <strong>Link a device</strong> →
                <strong> Link with phone number instead</strong>, then type this code.
              </p>
            </div>
          ) : (
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              loading={busy === 'Get code'}
              onClick={() => act('link-code', 'Get code')}
            >
              Get an 8-character code instead
            </Button>
          )}
        </div>
      )}

      <div className="cvm-actions">
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          // Disabled while syncing: this button is what undid the link once.
          disabled={!seat.configured || state === 'linking'}
          loading={busy === 'Reload page'}
          onClick={() => act('reload', 'Reload page')}
        >
          Reload WhatsApp Web
        </Button>
        <Button
          variant="danger"
          size="lg"
          fullWidth
          disabled={!seat.configured || state === 'linking' || session?.can_restart_chrome === false}
          loading={busy === 'Restart Chrome'}
          onClick={() =>
            act(
              'restart-chrome',
              'Restart Chrome',
              `Restart Chrome for ${seat.label}?\n\nThis does NOT log the seat out — the login is stored on the VM. The seat will be offline for about a minute.`
            )
          }
        >
          Restart Chrome
        </Button>
      </div>

      {session?.last_recovery_at && (
        <p className="cvm-footnote">
          Last automatic recovery {new Date(session.last_recovery_at).toLocaleString()}
          {session.reloads_in_window > 0 && ` · ${session.reloads_in_window} reload(s) in the last 15 min`}
          {session.reload_budget === 0 && ' · watchdog paused, needs a human'}
        </p>
      )}
    </Card>
  );
}

export function CoobyVmSettings() {
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // `rethrow` only from the poll below, so usePoll sees the failure; buttons
  // and SeatCard refreshes keep the old catch-and-show-inline behaviour.
  const load = useCallback(async ({ rethrow = false } = {}) => {
    try {
      const res = await axios.get(API, QUIET);
      setSeats(res.data?.seats || []);
      setError('');
    } catch (err) {
      setError(
        err.response?.status === 404
          ? 'This build of the app is newer than the API — the WhatsApp VMs endpoint is not deployed yet.'
          : err.response?.data?.message || 'Could not load the WhatsApp VM sessions.'
      );
      if (rethrow) throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Seat list every 10 s (usePoll, M4.1): backs off on errors, stops on
  // 403/404 (e.g. the endpoint not deployed yet), pauses in a hidden tab.
  const pollSeats = useCallback(() => load({ rethrow: true }), [load]);
  usePoll(pollSeats, { interval: LIST_POLL_MS, immediate: true });

  if (loading) return <StatePanel state="loading" title="Loading WhatsApp VM sessions…" />;

  return (
    <div className="container cooby-vm-settings">
      <header className="cvm-header">
        <h2>WhatsApp VMs</h2>
        <p>
          The Cooby seats run in a Chrome browser on two EC2 machines. When a seat
          drops, re-link it here instead of using Remote Desktop.
        </p>
      </header>

      {error ? (
        <StatePanel
          state="error"
          title="Could not load the WhatsApp VM sessions"
          description={error}
          onRetry={load}
        />
      ) : seats.length === 0 ? (
        <StatePanel state="empty" title="No Cooby seats are configured." />
      ) : (
        <div className="cvm-grid">
          {seats.map((seat) => (
            <SeatCard key={seat.id} seat={seat} onRefresh={load} />
          ))}
        </div>
      )}
    </div>
  );
}
